// The join page: where an invite link lands. The invite already knows the name, email and
// role; the person picks a password and is in, approved, the way v1's register form does it.
import { useEffect, useState, type FormEvent } from 'react';
import { createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { collection, doc, getDocs, limit, query, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ROLES, type Role } from '@tradeview/shared';
import { LEGACY_COMPANY_ID, paths } from '@tradeview/core';
import { auth, db, functions } from '../firebase';
import { ENV } from '../env';

interface Invite { id: string; code: string; firstName: string; lastName: string; email: string | null; role: Role; status: string; expiresAt?: { toMillis(): number } | null; startJobs?: Array<{ id: string; name: string }> }
type State = { kind: 'loading' } | { kind: 'bad'; why: 'missing' | 'unknown' | 'expired' | 'used' | 'revoked'; invite?: Invite } | { kind: 'ok'; invite: Invite } | { kind: 'exists'; invite: Invite } | { kind: 'done' };

export function Join({ code }: { code: string }) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [password, setPassword] = useState(''); const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState<string | null>(null); const [asked, setAsked] = useState(false);
  const p = paths(LEGACY_COMPANY_ID);
  useEffect(() => {
    let on = true;
    (async () => {
      const c = code.trim().toUpperCase();
      if (!c) { setState({ kind: 'bad', why: 'missing' }); return; }
      try {
        const snap = await getDocs(query(collection(db, p.collection('invites')), where('code', '==', c), limit(1)));
        if (!on) return;
        if (snap.empty) { setState({ kind: 'bad', why: 'unknown' }); return; }
        const d = snap.docs[0]; const inv = { id: d.id, ...(d.data() as Omit<Invite, 'id'>) };
        if (inv.status === 'used') { setState({ kind: 'bad', why: 'used', invite: inv }); return; }
        if (inv.status !== 'active') { setState({ kind: 'bad', why: 'revoked', invite: inv }); return; }
        if (inv.expiresAt?.toMillis && inv.expiresAt.toMillis() < Date.now()) { setState({ kind: 'bad', why: 'expired', invite: inv }); return; }
        setState({ kind: 'ok', invite: inv });
      } catch (e) { if (on) { setState({ kind: 'bad', why: 'unknown' }); setMsg(e instanceof Error ? e.message : String(e)); } }
    })();
    return () => { on = false; };
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  async function join(e: FormEvent) {
    e.preventDefault();
    if (state.kind !== 'ok') return;
    const inv = state.invite;
    if (!inv.email) { setMsg('This invite has no email on it. Ask the office to add one and send it again.'); return; }
    if (password.length < 8) { setMsg('Use at least 8 characters.'); return; }
    if (password !== again) { setMsg('The two passwords do not match.'); return; }
    setBusy(true); setMsg(null);
    try {
      const cred = await createUserWithEmailAndPassword(auth, inv.email.trim().toLowerCase(), password);
      const uid = cred.user.uid;
      try {
        await setDoc(doc(db, `${p.users}/${uid}`), { uid, firstName: inv.firstName, lastName: inv.lastName, email: inv.email.trim().toLowerCase(), role: inv.role, status: 'approved', createdAt: new Date().toISOString(), approvedAt: new Date().toISOString(), approvedBy: 'invite', inviteCode: inv.code, startJobs: inv.startJobs ?? [], app: 'v2' });
        await updateDoc(doc(db, `${p.collection('invites')}/${inv.id}`), { usedBy: uid, usedAt: serverTimestamp(), status: 'used' });
      } catch (err) {
        try { await cred.user.delete(); } catch { /* best effort */ }
        throw err;
      }
      setState({ kind: 'done' });   // the auth listener takes it from here
    } catch (err) {
      const c = (err as { code?: string })?.code ?? '';
      if (c.includes('email-already-in-use')) setState({ kind: 'exists', invite: inv });
      else if (c.includes('weak-password')) setMsg('Pick a stronger password.');
      else if (c.includes('network')) setMsg('No connection. Try again when you have signal.');
      else setMsg(err instanceof Error ? err.message : 'Something went wrong.');
    } finally { setBusy(false); }
  }
  async function askAgain() {
    setBusy(true);
    try { await httpsCallable(functions, 'teamAsk')({ code: code.trim().toUpperCase() }); setAsked(true); }
    catch (e) { setMsg(e instanceof Error ? e.message.replace(/^.*?: /, '') : String(e)); }
    finally { setBusy(false); }
  }
  async function reset(email: string) {
    setBusy(true);
    try { await sendPasswordResetEmail(auth, email); setMsg(`A reset link is on its way to ${email}.`); }
    catch (e) { setMsg(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }
  const phone = /Android|iPhone|iPad/i.test(navigator.userAgent);
  if (state.kind === 'loading') return <Frame msg={msg}><div className="skel h-24 rounded-[14px]" /></Frame>;
  if (state.kind === 'done') return <Frame msg={msg}><div className="card text-center"><b className="font-head font-bold block text-[16px]">You're in.</b><p className="m-0 text-muted text-[13.5px]">Loading your jobs…</p></div></Frame>;
  if (state.kind === 'exists') return (
    <Frame msg={msg}>
      <div className="card"><b className="font-head font-bold block text-[16px]">{state.invite.email} already has an account</b><p className="m-0 mt-1 text-muted text-[13.5px]">Sign in with it. If you forgot the password, we can send a reset link.</p>
        <div className="flex gap-2 mt-3"><a href="/" className="btn btn-primary flex-1">Sign in</a><button className="btn flex-1" disabled={busy} onClick={() => reset(state.invite.email!)}>Reset password</button></div></div>
    </Frame>
  );
  if (state.kind === 'bad') {
    const title = state.why === 'expired' ? 'This link has expired' : state.why === 'used' ? 'This link was already used' : state.why === 'revoked' ? 'This invite was cancelled' : state.why === 'missing' ? 'No invite code in the link' : 'That invite does not exist';
    const text = state.why === 'expired' ? 'Invite links work for 72 hours. Ask for a new one and the office gets a bell with a resend button.' : state.why === 'used' ? 'If that was you, sign in. If it was not, ask the office for a new link.' : 'Ask the office for a new invite link.';
    return (
      <Frame msg={msg}>
        <div className="card"><b className="font-head font-bold block text-[16px]">{title}</b><p className="m-0 mt-1 text-muted text-[13.5px]">{text}</p>
          {(state.why === 'expired' || state.why === 'used') && !asked && <button className="btn btn-primary btn-block mt-3" disabled={busy} onClick={askAgain}>{busy ? 'Asking…' : 'Ask for a new one'}</button>}
          {asked && <p className="m-0 mt-3 text-ok text-[13.5px] font-semibold">Asked. The office has been told; the new link will come by text or email.</p>}
          {state.why === 'used' && <a href="/" className="btn btn-block mt-2">Sign in</a>}</div>
      </Frame>
    );
  }
  const inv = state.invite;
  return (
    <Frame msg={msg}>
      <form onSubmit={join} className="flex flex-col gap-3">
        <div className="card"><b className="font-head font-bold block text-[17px]">Hi {inv.firstName}, you're invited.</b><p className="m-0 mt-1 text-[13.5px] text-muted">Joining as <b className="text-fg">{ROLES[inv.role]?.label ?? inv.role}</b>{inv.startJobs?.length ? <>, starting on <b className="text-fg">{inv.startJobs.map(j => j.name).join(', ')}</b></> : null}.</p></div>
        <label className="flex flex-col gap-1.5"><span className="font-head font-semibold text-[13px]">Your email</span><input className="field-in" type="email" value={inv.email ?? ''} readOnly /></label>
        <label className="flex flex-col gap-1.5"><span className="font-head font-semibold text-[13px]">Pick a password <span className="text-muted font-normal text-[12.5px]">8 characters or more</span></span><input className="field-in" type="password" autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} /></label>
        <label className="flex flex-col gap-1.5"><span className="font-head font-semibold text-[13px]">Same again</span><input className="field-in" type="password" autoComplete="new-password" value={again} onChange={e => setAgain(e.target.value)} required minLength={8} /></label>
        <button className="btn btn-primary btn-block" type="submit" disabled={busy || !inv.email}>{busy ? 'Setting up…' : 'Set up my account'}</button>
        <p className="m-0 text-[12.5px] text-muted">{phone ? 'Next the app asks for your location (it does not run without it), then add it to your home screen from the browser menu.' : 'Open this on your phone too: that is where the app lives. Add it to your home screen from the browser menu.'}</p>
      </form>
    </Frame>
  );
}

/** The page frame. Module scope on purpose: declared inside the screen it rebuilt on every keystroke and the password box lost its cursor. */
function Frame({ children, msg }: { children: React.ReactNode; msg: string | null }) {
  return (
    <div className="min-h-full flex flex-col">
      <div className="flex-1 flex flex-col justify-center gap-3.5 px-5 max-w-[420px] w-full mx-auto">
        <div className="flex flex-col items-center gap-2.5 mb-1">
          <div className="w-16 h-16 rounded-[18px] bg-accent text-accent-ink grid place-items-center font-disp font-bold text-4xl">T</div>
          <h1 className="font-disp font-bold text-[34px] leading-none m-0">TradeView</h1>
          <p className="m-0 text-muted text-sm">{ENV.name === 'production' ? 'Nextgen Electric NJ' : `${ENV.name} · ${ENV.firebase.projectId}`}</p>
        </div>
        {children}
        {msg && <div className="rounded-[12px] bg-crit-soft text-crit px-3 py-2.5 text-[13.5px]">{msg}</div>}
      </div>
      <p className="text-center text-muted text-xs pb-7">Already have an account? <a href="/" className="text-accent font-semibold">Sign in</a></p>
    </div>
  );
}
